import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { Chart, elements, registerables } from 'chart.js';
import { BeatLoader } from 'react-spinners';
import '../../../../../styles/count_graphs.css'
import { BiFullscreen, BiExitFullscreen, BiLineChart, BiBarChartAlt2 } from 'react-icons/bi';


const LprMonthlyCountChart = ({
    selectedYear,
    monthlyLprCountData,
    monthlyDataLoading,
    selectedMonth,
    handleMonthlyBarClick,
    monthlyVehiclePercentageIncrease,
    monthlyVehicles
}) => {
    const [barChartData, setBarChartData] = useState();
    const [lineChartData, setLineChartData] = useState();
    const [chartType, setChartType] = useState('bar');
    const barChartRef = useRef(null);
    const lineChartRef = useRef(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const getMonthName = (monthNumber) => {
        const months = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];
        return months[monthNumber - 1];
    };

    const smallScreen = window.innerWidth < 786;

    const prepareMonthlyLprCountLineChart = () => {
        return {
            labels: monthlyLprCountData.map(d => getMonthName(d.month)),
            datasets: [
                {
                    label: 'Monthly LPR Count',
                    data: monthlyLprCountData.map(d => d.total_count),
                    backgroundColor: '#FFC400',
                    borderColor: '#FFC400',
                    borderWidth: 3,
                    tension: 0.3
                }
            ]
        };
    };

    const getMonthlyLprCountLineChartOptions = () => {
        return {
            responsive: true, 
            onClick: (event, elements) => {
                if (elements.length > 0) {
                    const index = elements[0].index;
                    const monthNumber = monthlyLprCountData[index].month;
                    handleMonthlyBarClick(monthNumber);
                }
            },
            hover: {
                mode: 'nearest',
                intersect: false,
            },
            onHover: function (event, elements) {
                const chartElement = this.canvas;
                if (elements && elements.length > 0) {
                    chartElement.style.cursor = 'pointer';
                } else {
                    chartElement.style.cursor = 'default';
                }
            },
            elements: {
                points: {
                    radius: 4,
                    hoverRadius: 6
                }
            },
            maintainAspectRatio: true, 
            scales: {
                x: {
                    ticks: {
                        color: (context) => {
                            const labelMonth = getMonthName(monthlyLprCountData[context.index]?.month);
                            return labelMonth === getMonthName(selectedMonth) ? '#32de84' : '#fff';
                        },
                        font: {
                            weight: (context) => context.tick.label === getMonthName(selectedMonth) ? 'bold' : 'normal',
                            size: (context) => context.tick.label === getMonthName(selectedMonth) ? 16 : 14
                        },
                    },
                    title: {
                        display: true,
                        text: 'Months',
                        color: 'white'
                    },
                    grid: {
                        display: true,
                        drawBorder: true,
                        drawOnChartArea: false,
                        color: '#aaa'
                    }
                },
                y: {
                    ticks: {
                        color: '#F8F8F8',
                    },
                    title: {
                        display: true,
                        text: 'LPR Count',
                        color: 'white'
                    },
                },
            },
            plugins: {
                tooltip: {
                    mode: 'nearest', 
                },
                legend: {
                    position: 'top', 
                    labels: {
                        color: 'white',
                        font: {
                            size: smallScreen ? 8 : 14, 
                        },
                        boxWidth: smallScreen ? 8 : 40,  
                        boxHeight: smallScreen ? 8 : 10, 
                    },
                },
            },

        }
    }

    const getMonthlyLprCountBarChartOptions = () => {
        return {
            responsive: true, 
            onClick: (event, elements) => {
                if (elements.length > 0) {
                    const index = elements[0].index;
                    const monthNumber = monthlyLprCountData[index].month;
                    handleMonthlyBarClick(monthNumber);
                }
            },
            hover: {
                mode: 'nearest',
                intersect: false,
            },
            onHover: function (event, elements) {
                const chartElement = this.canvas;
                if (elements && elements.length > 0) {
                    chartElement.style.cursor = 'pointer';
                } else {
                    chartElement.style.cursor = 'default';
                }
            },
            maintainAspectRatio: true, 
            scales: {
                x: {
                    stacked: true,
                    ticks: {
                        color: (context) => {
                            const labelMonth = getMonthName(monthlyLprCountData[context.index]?.month);
                            return labelMonth === getMonthName(selectedMonth) ? '#32de84' : '#fff';
                        },
                        font: {
                            weight: (context) => context.tick.label === getMonthName(selectedMonth) ? 'bold' : 'normal',
                            size: (context) => context.tick.label === getMonthName(selectedMonth) ? 16 : 14
                        },
                    },
                    title: {
                        display: true,
                        text: 'Months',
                        color: 'white'
                    },
                    grid: {
                        display: true,
                        drawBorder: true,
                        drawOnChartArea: false,
                        color: '#aaa'
                    }
                },
                y: {
                    ticks: {
                        color: '#F8F8F8',
                    },
                    stacked: false,
                    title: {
                        display: true,
                        text: 'LPR Count',
                        color: 'white'
                    },
                },
            },
            plugins: {
                tooltip: {
                    mode: 'nearest', 
                },
                legend: {
                    position: 'top', 
                    labels: {
                        color: 'white',
                        font: {
                            size: smallScreen ? 8 : 14, 
                        },
                        boxWidth: smallScreen ? 8 : 40,  
                        boxHeight: smallScreen ? 8 : 10, 
                    },
                },
            },
        }
    }

    const prepareMonthlyLprCountBarChart = () => {
        return {
            labels: monthlyLprCountData.map(d => getMonthName(d.month)),
            datasets: [
                {
                    label: 'Monthly LPR Count',
                    data: monthlyLprCountData.map(d => d.total_count),
                    backgroundColor: '#FFC400',
                    borderColor: '#FFC400',
                    borderWidth: {
                        top: 0,
                        right: monthlyLprCountData.map(d => (selectedMonth === d.month ? 2 : 0)),
                        bottom: 0,
                        left: monthlyLprCountData.map(d => (selectedMonth === d.month ? 2 : 0)),
                    },
                }
            ]
        };
    };

    const toggleChartType = () => {
        setChartType(prevType => prevType === 'bar' ? 'line' : 'bar');
    };

    useEffect(() => {
        if (monthlyLprCountData.length) {
            const preparedBarChart = prepareMonthlyLprCountBarChart();
            if (preparedBarChart) {
                setBarChartData(preparedBarChart);
            }
            const preparedLineChart = prepareMonthlyLprCountLineChart();
            if (preparedLineChart) {
                setLineChartData(preparedLineChart);
            }
        }
    }, [monthlyLprCountData]);

    Chart.register(...registerables);

    return (
        <div className={`count-bar-chart-wrapper ${isFullscreen ? 'fullscreen-container' : ''}`}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>

            <div className='graph-title' style={{ flex: '1 1 auto', minWidth: '200px' }}>
                Monthly LPR Count
            </div>

            <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    justifyContent: 'flex-end',
                    flex: '1 1 auto',
                    minWidth: '100px',
                }}>                
                <button className='toggle-chart-button' onClick={() => toggleChartType('month')}>
                {chartType ? (
                    <BiBarChartAlt2 size={24} title='Switch to Bar Chart' color='white' />
                ) : (
                    <BiLineChart size={24} title='Switch to Line Chart' color='white' />
                )}
                </button>

                <button
                className='toggle-chart-button'
                onClick={() => setIsFullscreen(prev => !prev)}
                title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                >
                {isFullscreen ? (
                    <BiExitFullscreen size={24} color="white" />
                ) : (
                    <BiFullscreen size={24} color="white" />
                )}
                </button>

                <div className='count-chart-footer-percentage-container'>
                <div className='count-chart-footer-percentage-icon'>
                    {monthlyVehiclePercentageIncrease > 0 ? (
                    <svg
                        style={{ color: '#32de84' }}
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        className="bi bi-arrow-up-right-circle"
                        viewBox="0 0 16 16"
                    >
                        <path
                        fillRule="evenodd"
                        d="M1 8a7 7 0 1 0 14 0A7 7 0 0 0 1 8zm15 0A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.854 10.803a.5.5 0 1 1-.708-.707L9.243 6H6.475a.5.5 0 1 1 0-1h3.975a.5.5 0 0 1 .5.5v3.975a.5.5 0 1 1-1 0V6.707l-4.096 4.096z"
                        />
                    </svg>
                    ) : (
                    <svg
                        style={{ color: '#EF5B5B' }}
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        className="bi bi-arrow-down-right-circle"
                        viewBox="0 0 16 16"
                    >
                        <path
                        fillRule="evenodd"
                        d="M1 8a7 7 0 1 0 14 0A7 7 0 0 0 1 8zm15 0A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.854 5.146a.5.5 0 1 0-.708.708L9.243 9.95H6.475a.5.5 0 1 0 0 1h3.975a.5.5 0 0 0 .5-.5V6.475a.5.5 0 1 0-1 0v2.768L5.854 5.146z"
                        />
                    </svg>
                    )}
                </div>
                <div
                    className='count-chart-footer-percentage-value'
                    style={{ color: monthlyVehiclePercentageIncrease > 0 ? '#32de84' : '#EF5B5B' }}
                >
                    {monthlyVehiclePercentageIncrease}%
                </div>
                </div>
            </div>
        </div>
            <div className='chart-total-container'>
                <div className='chart-total-pill'>
                    <div className="chart-total-label" style={{ color: '#FFC400' }}>Total: {monthlyLprCountData.reduce((acc, curr) => acc + curr.total_count, 0)}</div>
                </div>
            </div>
            <div className={monthlyDataLoading ? 'monthly-bar-chart-container-loading' : ''}>
                {
                    chartType === 'bar' ? 
                        <Bar
                            ref={barChartRef}
                            data={barChartData || { labels: [], datasets: [] }} // Fetch prepared data
                            options={getMonthlyLprCountBarChartOptions()}
                        />
                        :
                        <Line 
                            ref={lineChartRef}
                            data={lineChartData || { labels: [], datasets: [] }} // Fetch prepared data
                            options={getMonthlyLprCountLineChartOptions()}
                        />

                }
                {/* <div className='chart-footer-container'>
                    <div className='chart-footer-label'>{monthlyVehicles} vehicles detected for {getMonthName(selectedMonth)} {selectedYear}</div>
                </div> */}
                {
                    monthlyDataLoading && <div id="monthly-loading-overlay">
                        <BeatLoader color={'white'} loading={monthlyDataLoading} size={15} />
                    </div>
                }
            </div>
        </div>
    )
}


export default LprMonthlyCountChart;